import { forwardRef, Fragment, useRef, useState } from "react";

import clsx from "clsx";
import moment from "moment";
import { Controller, type UseFormReturn } from "react-hook-form";
import { useOnClickOutside } from "usehooks-ts";

import { Tooltip } from "@components/tooltip-container";
import { formatDate } from "@features/mmm/utils/formatters";
import {
  addDaysToIsoDate,
  getClosestUpcomingTuesday,
} from "@features/mmm/utils/get_cycle_weeks";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import { renderChevronAnimation } from "@shared/selects/utils";

import { type MediaPlan } from "./create-media-plan-modal";
import { DateRangeCalendar } from "./date-range-calendar";

interface Props {
  form: UseFormReturn<MediaPlan>;
}

const MONDAY = 1;
const TUESDAY = 2;

export const MediaPlanDateRangeField = ({ form }: Props) => {
  const [isDateRangeCalendarOpen, setIsDateRangeCalendarOpen] = useState(false);

  return (
    <div>
      <div className="text-sm text-interstellar-1 mb-2">
        Choose the date range that you want a forecast and budgets for
      </div>
      <div className="flex items-center gap-8">
        <Controller
          control={form.control}
          name="start_date"
          render={({ field: startDateField }) => {
            return (
              <Controller
                control={form.control}
                name="end_date"
                render={({ field: endDateField }) => (
                  <>
                    <div className="nb-input-container w-fit">
                      <label className="label">
                        DATE RANGE<span className="text-mars">*</span>
                      </label>
                      <div className="flex items-center gap-1">
                        <div
                          className="flex items-center cursor-pointer text-sm"
                          onClick={() =>
                            setIsDateRangeCalendarOpen((prev) => !prev)
                          }
                        >
                          <i className="fa-regular fa-calendar text-cosmic mr-2" />
                          <span className="font-semibold whitespace-nowrap tracking-wider underline text-cosmic">
                            {`${
                              endDateField.value && startDateField.value
                                ? `${formatDate(startDateField.value)} -
                                 ${formatDate(
                                   addDaysToIsoDate(
                                     getClosestUpcomingTuesday(
                                       endDateField.value,
                                     ),
                                     -1,
                                   ),
                                 )}`
                                : "Select date"
                            }`}
                          </span>
                        </div>
                        <MediaPlanDateRangePicker
                          startDate={startDateField.value}
                          endDate={endDateField.value}
                          onStartDateChange={startDateField.onChange}
                          onEndDateChange={endDateField.onChange}
                          isCalendarOpen={isDateRangeCalendarOpen}
                          setIsCalendarOpen={setIsDateRangeCalendarOpen}
                          renderDay={{
                            endDayRenderDay: (date) => {
                              const dayOfMonth = date.getDate();
                              const day = date.getDay();
                              const today = moment().startOf("day");
                              return (
                                <Tooltip
                                  content="Plans must end on a Monday"
                                  showTooltip={
                                    day !== MONDAY &&
                                    moment(date).isAfter(today)
                                  }
                                  noInfoCircle
                                  placement="top"
                                  mouseEnterDelay={1000}
                                >
                                  <div className="h-full w-full flex-center">
                                    {dayOfMonth}
                                  </div>
                                </Tooltip>
                              );
                            },
                            startDayRenderDay: (date) => {
                              const dayOfMonth = date.getDate();
                              const day = date.getDay();
                              const today = moment().startOf("day");
                              return (
                                <Tooltip
                                  content="Plans must start on a Tuesday"
                                  showTooltip={
                                    day !== TUESDAY &&
                                    moment(date).isAfter(today)
                                  }
                                  noInfoCircle
                                  placement="top"
                                  mouseEnterDelay={1000}
                                >
                                  <div className="h-full w-full flex-center">
                                    {dayOfMonth}
                                  </div>
                                </Tooltip>
                              );
                            },
                          }}
                          excludeStartDate={(date) => {
                            const res = date.getDay() !== TUESDAY;
                            return res;
                          }}
                          excludeEndDate={(date) => date.getDay() !== MONDAY}
                        />
                      </div>
                    </div>
                  </>
                )}
              />
            );
          }}
        />
      </div>
    </div>
  );
};

const MAX_LOOKBACK_DAYS = 0;
const MAX_ALLOWED_DATE = moment().add(1, "year");

export const MediaPlanDateRangePicker = forwardRef<
  HTMLElement,
  {
    startDate: string | null;
    endDate: string | null;
    onStartDateChange: (value: string) => void;
    onEndDateChange: (value: string) => void;
    isCalendarOpen: boolean;
    setIsCalendarOpen: (isCalendarOpen: boolean) => void;
    excludeStartDate?: (date: Date) => boolean;
    excludeEndDate?: (date: Date) => boolean;
    description?: string;
    renderDay?: {
      startDayRenderDay: (date: Date) => React.ReactNode;
      endDayRenderDay: (date: Date) => React.ReactNode;
    };
  }
>(
  (
    {
      startDate,
      endDate,
      onStartDateChange,
      onEndDateChange,
      isCalendarOpen,
      setIsCalendarOpen,
      excludeStartDate,
      excludeEndDate,
      description,
      renderDay,
    },
    forRef,
  ) => {
    const ref = useRef(null);

    useOnClickOutside(ref, () => setIsCalendarOpen(false));

    return (
      <Popover ref={forRef} className="nb-input-container relative">
        <PopoverButton
          className="hover:bg-cosmic-4 rounded-md w-6 h-6"
          onClick={() => setIsCalendarOpen(!isCalendarOpen)}
        >
          {renderChevronAnimation(isCalendarOpen, false, null, "text-cosmic")}
        </PopoverButton>
        {isCalendarOpen && (
          <Transition
            show={isCalendarOpen}
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <PopoverPanel
              static
              ref={ref}
              anchor="bottom"
              className={clsx(
                "z-[9998] mt-0 elevation-1",
                "rounded-md py-1 bg-v2-primary flex flex-col space-y-2 w-fit",
              )}
            >
              {description && (
                <div className="text-xs relative pt-2 pl-4">
                  <span className="absolute top-1/2">{description}</span>
                </div>
              )}
              <DateRangeCalendar
                maxLookbackDays={MAX_LOOKBACK_DAYS}
                maxAllowedDate={MAX_ALLOWED_DATE}
                className={clsx(
                  "[&_.day[data-selected]]:bg-cosmic",
                  "[&_.day[data-selected]]:text-white",
                  "[&_.day[data-selected]]:rounded-lg",
                )}
                value={{ startDate, endDate }}
                onApply={(val) => {
                  onStartDateChange(val.startDate);
                  onEndDateChange(val.endDate);
                  setIsCalendarOpen(false);
                }}
                excludeStartDate={excludeStartDate}
                excludeEndDate={excludeEndDate}
                renderDay={renderDay}
              />
            </PopoverPanel>
          </Transition>
        )}
      </Popover>
    );
  },
);
